import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Terminal, Cpu, Lock } from "lucide-react";
import { api, setSEO } from "@/lib/api";
import BrandMarquee from "@/components/BrandMarquee";
import ProductCard from "@/components/ProductCard";
import FAQAccordion from "@/components/FAQAccordion";
import DropListForm from "@/components/DropListForm";
import CountdownTimer from "@/components/CountdownTimer";
import VoidAtmosphere from "@/components/VoidAtmosphere";

const FAQ = [
  {
    q: "What is FEMBOT NEX?",
    a: "FEMBOT NEX is a US-based, limited-run post-ironic activewear brand. Cyber-femme gymwear, mesh layers, biker shorts, sports bras, crop tops, and offensive little statements for women who lift, spiral, and still look expensive doing it.",
  },
  {
    q: "Is FEMBOT NEX activewear or streetwear?",
    a: "Both. Mainline pieces are technical gymwear engineered for compression and sweat. They're cut and styled to live outside the gym too — for car parks, mirror checks, and emotional system failure.",
  },
  {
    q: "Where is FEMBOT NEX based?",
    a: "United States. All prices in USD. US-domestic shipping. International transmission gates open after Drop 001.",
  },
  {
    q: "Are prices in USD?",
    a: "Yes. All prices are USD. Some pieces are preorder-based — timelines listed on each product file before checkout opens.",
  },
  {
    q: "What is a limited drop?",
    a: "Numbered runs. No restocks. When a drop is gone, it is gone. Production ships 2–4 weeks after the preorder window closes.",
  },
  {
    q: "What is the Contraband collection?",
    a: "Where the less behaved files go. Limited-run satirical pieces, dark humor, bad taste. Restricted access, final sale. If you need the clothes to behave, shop Mainline.",
  },
  {
    q: "Are the clothes made for working out?",
    a: "Yes. Mainline pieces are squat-tested, four-way stretch, sweat-wicking, and compression-rated. Wear them in the gym, the parking lot, or to brunch with your situationship.",
  },
  {
    q: "How do FEMBOT NEX biker shorts fit?",
    a: "Snug, never sheer. Contoured high-rise waistband. Squat-tested for opacity. True to size — size up only for a softer fit.",
  },
  {
    q: "How do FEMBOT NEX sports bras fit?",
    a: "Compressive medium-impact hold. True to size for lifts. Size up for a more relaxed fit.",
  },
  {
    q: "When does Drop 001 launch?",
    a: "Drop 001: Controlled Malfunction is in pre-drop. Authorize uplink for the launch transmission, restock pings, and contraband access requests.",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    setSEO({
      title: "FEMBOT NEX | Offensive Cyber-Femme Activewear & Post-Ironic Gymwear",
      description:
        "FEMBOT NEX makes limited-run cyber-femme activewear, offensive gymwear, crop tops, sports bras, biker shorts, joggers, mesh layers, and post-ironic clothes for emotional system failure.",
    });
    api.get("/drops/001").then((r) => setFeatured(r.data.products.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden noise scanlines">
        <VoidAtmosphere intensity="hero" />
        <div className="absolute inset-0 hairline-grid opacity-40 pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-20 md:pb-32 relative">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span data-testid="hero-emblem-boot" className="emblem-boot">
              <img
                src="/brand/emblem.png"
                alt="FEMBOT NEX emblem"
                width={44}
                height={44}
                className="drop-shadow-[0_0_18px_rgba(255,20,147,0.7)]"
                draggable={false}
              />
            </span>
            <span className="badge badge-live" data-testid="hero-signal">Signal // Live</span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#c084fc]">
              // POST HOMINEM · TX_001
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[44px] sm:text-[68px] md:text-[110px] lg:text-[140px] leading-[0.85] tracking-tighter glitch"
          >
            FEMBOT
            <br />
            <span className="shimmer-text">NEX<span className="text-[#ff1493]">.</span></span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 font-body text-lg md:text-2xl max-w-2xl text-white/90 leading-snug"
          >
            Post-ironic activewear for{" "}
            <span className="text-[#ff1493]">emotional system failure</span>.
          </p>

          <p className="mt-4 max-w-xl text-sm md:text-base text-[#b4a3c7] leading-relaxed">
            Limited-run cyber-femme gymwear, mesh layers, biker shorts, sports bras, tanks, joggers, and offensive little statements for the beautifully unwell.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/drop-001" data-testid="hero-shop-drop-001" className="btn-primary">
              Shop Drop 001 <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link to="/contraband" data-testid="hero-enter-contraband" className="btn-outline">
              <Lock size={14} strokeWidth={1.5} /> Enter Contraband
            </Link>
            <a href="#drop-list" data-testid="hero-join-drop-list" className="btn-ghost">
              Join Drop List →
            </a>
          </div>

          {/* Status terminal block */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 border border-[#2a1a3a] bg-[#05000f]/65 backdrop-blur-sm">
            {[
              ["Mainline", "Active"],
              ["Contraband", "Restricted"],
              ["Currency", "USD"],
              ["Launch", "Pre-Drop"],
            ].map(([k, v]) => (
              <div key={k} className="p-4 md:p-6 border-r border-b md:border-b-0 border-[#2a1a3a] last:border-r-0">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#7a6a90]">{k}</div>
                <div className="mt-2 font-display text-lg md:text-xl tracking-tight">{v}</div>
              </div>
            ))}
          </div>

          {/* Countdown */}
          <div className="mt-8 max-w-2xl">
            <CountdownTimer joinHref="/#drop-list" />
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* DROP 001 INTRO */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-40">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="section-label">// SS26 / Transmission 001</div>
            <div className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase text-[#9d4cdd]">
              The first archive.
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.0]">
              Drop 001: <span className="text-[#ff1493]">Controlled Malfunction</span>
            </h2>
            <p className="mt-8 text-[16px] md:text-[18px] text-[#c4b6d6] leading-[1.7] max-w-2xl">
              Six pieces of cyber-femme performancewear. Compression-adjacent chaoswear for gym girls, void walkers, and anyone currently buffering through reality. Preorder window open. Production ships in 2–4 weeks.
            </p>
            <div className="mt-10 flex gap-3">
              <Link to="/drop-001" data-testid="drop-intro-enter" className="btn-primary">
                Enter Drop 001 <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/shop" data-testid="drop-intro-shop-all" className="btn-outline">
                Shop All
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-t border-[#2a1a3a]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <div>
              <div className="section-label">// Featured</div>
              <h3 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-2xl">
                The uniform of <span className="text-[#ff1493]">controlled malfunction</span>.
              </h3>
            </div>
            <Link to="/shop" data-testid="featured-shop-all" className="hidden md:inline-flex btn-ghost">
              Shop All →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
          <div className="mt-10 font-mono text-[11px] tracking-[0.24em] uppercase text-[#7a6a90]">
            // Editorial prototype visuals. Final sample photos coming soon.
          </div>
        </div>
      </section>

      {/* MAINLINE VS CONTRABAND */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-40">
        <div className="section-label">// Three brand pillars</div>
        <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-[1.0] max-w-3xl">
          What FEMBOT NEX is, in three transmissions.
        </h2>

        <div className="mt-16 grid md:grid-cols-3 gap-8 md:gap-10">
          {[
            {
              n: "01",
              icon: <Cpu size={20} strokeWidth={1.25} />,
              title: "Cyber-Femme Performance",
              body: "Technical gymwear engineered for compression, sweat, and range of motion. Styled like a chaos agent in a car park.",
            },
            {
              n: "02",
              icon: <Terminal size={20} strokeWidth={1.25} />,
              title: "Post-Ironic Slogans",
              body: "Every piece says something. Some flirt with collapse. All of them are social satire.",
            },
            {
              n: "03",
              icon: <Lock size={20} strokeWidth={1.25} />,
              title: "Limited Drops Only",
              body: "Numbered runs. No restocks. No warehouse bloat. When a drop is gone, it is gone.",
            },
          ].map((p) => (
            <div
              key={p.n}
              className="border border-[#2a1a3a] bg-[#0a0316]/40 backdrop-blur-sm p-8 md:p-10 transition-colors duration-300 hover:border-[#ff1493]/40"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#ff1493]">
                  {p.n} / Pillar
                </span>
                <span className="text-[#ff1493]">{p.icon}</span>
              </div>
              <h4 className="mt-8 font-display text-xl md:text-2xl tracking-tight leading-tight">
                {p.title}
              </h4>
              <p className="mt-4 text-[15px] text-[#b4a3c7] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-t border-[#2a1a3a] relative overflow-hidden">
        <VoidAtmosphere intensity="subtle" />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-32 grid md:grid-cols-12 gap-10 relative">
          <div className="md:col-span-4">
            <div className="section-label">// MANIFESTO</div>
            <div className="mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-[#7a6a90]">
              $ cat manifesto.txt
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              Fashion is dead. <br />
              <span className="text-[#ff1493]">We are what remains.</span>
            </h2>
            <ol className="mt-10 space-y-4 md:space-y-2">
              {[
                "We reject the seasonal cycle. Every piece is a permanent statement.",
                "The body is not a canvas. It is the void through which cloth speaks.",
                "Post-irony is not nihilism. It is the discipline of sincerity without apology.",
              ].map((line, i) => (
                <li key={i} className="manifesto-row">
                  <span className="manifesto-num">{String(i + 1).padStart(3, "0")}</span>
                  <span className="manifesto-body">{line}</span>
                </li>
              ))}
            </ol>
            <div className="mt-10">
              <Link to="/about" data-testid="manifesto-consume" className="btn-outline">
                Consume in Full <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DROP LIST */}
      <section id="drop-list" className="relative overflow-hidden">
        <div className="absolute inset-0 hairline-grid opacity-40 pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-[#ff1493] opacity-[0.10] blur-[100px]" />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28 relative">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7">
              <div className="section-label">// PROTOCOL_0x01</div>
              <h2 className="mt-3 font-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                [ Authorize access to <span className="text-[#ff1493]">Drop 001</span> ]
              </h2>
              <p className="mt-6 text-base md:text-lg text-[#a3a3a3] leading-relaxed max-w-2xl">
                Drop 001: Controlled Malfunction. Six pieces of cyber-femme activewear for the beautifully unwell. Authorize your uplink for preorder access, restock notifications, and contraband transmissions.
              </p>
              <div className="mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[#6b6b6b]">
                // we just ket each other
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="border border-[#262626] bg-black/60 backdrop-blur-sm p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">
                    // UPLINK FORM
                  </div>
                  <span className="badge badge-live">Open</span>
                </div>
                <DropListForm source="homepage_drop_list" testIdPrefix="home-drop" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <div className="section-label">// FAQ</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-[1.0]">
              Frequently asked <span className="text-[#ff1493]">malfunctions</span>.
            </h2>
            <p className="mt-6 text-[15px] text-[#b4a3c7] leading-relaxed max-w-sm">
              Answers for the beautifully unwell. Still confused? Open a transmission via Contact.
            </p>
          </div>
          <div className="md:col-span-8">
            <FAQAccordion items={FAQ} testIdPrefix="home-faq" />
          </div>
        </div>
      </section>
    </div>
  );
}
